import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

export enum DataLayerProperties {
  Event = 'event',
  VirtualPageUrl = 'virtualPageURL',
  VirtualPageTitle = 'virtualPageTitle',
  AffiliateCode = 'affiliateCode',
  TransactionId = 'transactionId',
  TransactionAffiliation = 'transactionAffiliation',
  TransactionTotal = 'transactionTotal',
  TransactionCurrency = 'transactionCurrency',
  QuotationDestination = 'QuotationDestination',
  QuotationDeparture = 'QuotationDeparture',
  QuotatioPassengers = 'QuotatioPassengers',
  QuotationRequestedDate = 'QuotationRequestedDate',
  QuotationResult = 'QuotationResult',
  QuotationPrices = 'QuotationPrices',
}

export type FullDataLayer = {
  [key in DataLayerProperties]: string | number |  undefined;
};

export interface PartialDataLayer {
  [key: string]: string | number;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private dataLayer: FullDataLayer;
  public localLayer: any[];

  constructor(private router: Router) {
    this.generateEmptyDataLayer();
  }

  pageView(overridePath?: string, overrideTitle?: string, skipUmami: boolean = false, data?: Record<string, any>): void {
    const path = overridePath || this.router.url;
    const title = overrideTitle || overridePath || this.router.url;
    if((window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'VirtualPageView',
        virtualPageURL: path,
        virtualPageTitle: title,
      });
    }
    this.localLayer = (window as any).dataLayer;
    // `order-processed` mirrors the `booking_completed` step in the Umami funnel,
    // so callers can skip the Umami page view to avoid a duplicate funnel step.
    // `data` is attached to the mirrored Umami step EVENT (non-PII context).
    if (!skipUmami) {
      this.trackUmamiPageView(path, data);
    }
  }

  public addToDataLayer(properties: PartialDataLayer, pushDataLayer: boolean = false): void {
    const dataLayerObject = {};
    Object.keys(DataLayerProperties).map(key => DataLayerProperties[key]).forEach(key => {
      dataLayerObject[key] = properties[key];
    });
    this.pushDataLayer(dataLayerObject);
    this.trackUmamiEvent(properties);
  }

  private pushDataLayer(dataLayerObject): void {
    if((window as any).dataLayer) {
      (window as any).dataLayer.push(dataLayerObject);
    }
    this.localLayer = (window as any).dataLayer;
  }

  private generateEmptyDataLayer(): void {
    this.dataLayer = {
      [DataLayerProperties.Event]: undefined,
      [DataLayerProperties.VirtualPageUrl]: undefined,
      [DataLayerProperties.VirtualPageTitle]: undefined,
      [DataLayerProperties.AffiliateCode]: undefined,
      [DataLayerProperties.TransactionId]: undefined,
      [DataLayerProperties.TransactionAffiliation]: undefined,
      [DataLayerProperties.TransactionTotal]: undefined,
      [DataLayerProperties.TransactionCurrency]: undefined,
      [DataLayerProperties.QuotationDestination]: undefined,
      [DataLayerProperties.QuotationDeparture]: undefined,
      [DataLayerProperties.QuotatioPassengers]: undefined,
      [DataLayerProperties.QuotationRequestedDate]: undefined,
      [DataLayerProperties.QuotationResult]: undefined,
      [DataLayerProperties.QuotationPrices]: undefined,
    };
  }

  /**
   * Send a page view to Umami only (not to the GTM dataLayer). Used for the
   * initial form-load view: at form-load time the deferred Umami script has not
   * finished loading yet, so the regular pageView() drops the Umami mirror.
   * The caller fires this from the script's onload, once umami is available.
   */
  public umamiPageView(path: string, data?: Record<string, any>): void {
    this.trackUmamiPageView(path, data);
  }

  /**
   * Register a single Umami PAGEVIEW for the web-booker load.
   *
   * Umami auto-track is disabled (see createUmami) and the funnel runs entirely
   * on named custom EVENTS, so no pageview is emitted otherwise. But the portal's
   * website-traffic panel reads Umami's pageview-based visitor stats
   * (GET /websites/:id/stats), which count nothing without a pageview — so a
   * booker load showed 0 visitors even though the funnel events were recorded.
   * This sends exactly ONE pageview per load with a FIXED url/title so visits
   * aggregate (each form already has its own umamiWebsiteId, so there's no need
   * to encode the form/language in the path). It does NOT affect the funnel: a
   * pageview carries no event name, so it never matches an event-type funnel step.
   *
   * Passing a function to umami.track() overrides the default page payload and
   * sends it as a pageview (a string arg would instead send a named event).
   */
  public umamiVisit(url: string = '/webbooker', title: string = 'Web Booker'): void {
    const umami = (window as any).umami;
    if (umami && typeof umami.track === 'function') {
      umami.track((payload: Record<string, any>) => ({ ...payload, url, title }));
    }
  }

  /**
   * Fire a named Umami custom event directly (no GTM dataLayer push). Use for
   * events that have no GTM equivalent — e.g. negative actions (quotation_failed,
   * workarea_failed). No-op when the tracking script isn't loaded yet.
   */
  public umamiEvent(name: string, data?: Record<string, any>): void {
    const umami = (window as any).umami;
    if (umami && typeof umami.track === 'function') {
      umami.track(this.normalizeEventName(name), data || {});
    }
  }

  /**
   * Normalise every Umami event name to one consistent format: lowercase, with
   * `_` as the only separator (hyphens/spaces become underscores) and any other
   * character stripped. This keeps the whole funnel uniform (form_loaded,
   * product_selection_loaded, …, booking_completed, payment_initiated,
   * payment_failed) regardless of how a caller spells the
   * name or what a server-configured `gtmVirtualPageUrl` looks like. Applies to
   * the Umami side only — the GTM dataLayer keeps its original event names.
   */
  private normalizeEventName(name: string): string {
    return (name || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  /**
   * Fire a Umami funnel step as a single NAMED custom event (when the tracking
   * script is loaded). Used for the load step (form_loaded / widget_loaded).
   *
   * The whole booking wizard is one virtual page (no real URL changes), so the
   * funnel is driven by named events, not URL pageviews — Umami auto-track is
   * disabled (see createUmami). We deliberately fire ONE event per step and NO
   * pageview mirror, so each funnel step is exactly one send: form_loaded,
   * product_selection_loaded, …, booking_completed. `data` carries structured
   * (non-PII) context. The event name is the last path segment (e.g.
   * "form_loaded"), normalised for consistency.
   */
  private trackUmamiPageView(path: string, data?: Record<string, any>): void {
    const umami = (window as any).umami;
    if (umami && typeof umami.track === 'function') {
      const step = this.toUmamiStepName(path);
      const eventName = this.normalizeEventName(step.replace(/^\//, '') || 'page');
      umami.track(eventName, data || {});
    }
  }

  /** Reduce a virtual page URL to its last segment (the step name), e.g.
   *  "/nl/63bd.../product-selection" -> "/product-selection". */
  private toUmamiStepName(path: string): string {
    const segments = (path || '').split('/').filter(Boolean);
    const step = segments.length ? segments[segments.length - 1] : '';
    return step ? `/${step}` : '/';
  }

  /**
   * Mirror a dataLayer event (e.g. 'Quotation', 'Booking') to Umami as a named
   * custom event, carrying the same properties (minus the event name itself).
   */
  private trackUmamiEvent(properties: PartialDataLayer): void {
    const umami = (window as any).umami;
    if (!umami || typeof umami.track !== 'function') {
      return;
    }
    const eventName = (properties[DataLayerProperties.Event] as string) || 'event';
    const eventData: Record<string, any> = {};
    Object.keys(properties).forEach(key => {
      if (key !== DataLayerProperties.Event && properties[key] !== undefined && properties[key] !== '') {
        eventData[key] = properties[key];
      }
    });
    umami.track(this.normalizeEventName(eventName), eventData);
  }

}
